import { useTranslation } from 'react-i18next';
import { ActionBar } from '@/components/ActionBar';
import { ActionType, DividerOrientation } from '@/components/ActionBar';
import type { ActionConfig } from '@/components/ActionBar';
import { useActionBarResponsiveClasses } from '@/components/ActionBar/useActionBarResponsiveClasses';
import { Checkbox, CheckboxVariant } from '@/components/Checkbox';
import { I18nNamespace } from '@/lib/i18n-namespaces';

interface SelectionBarProps {
    selectedCount: number;
    totalCount?: number;
    entityLabel?: string;
    onSelectAll: () => void;
    onExitMultiSelect: () => void;
    bulkActions?: ActionConfig[];
    useShortSelectionCount?: boolean;
}

export function SelectionBar({
    selectedCount,
    totalCount,
    entityLabel,
    onSelectAll,
    onExitMultiSelect,
    bulkActions = [],
    useShortSelectionCount = false,
}: SelectionBarProps) {
    const { t } = useTranslation([I18nNamespace.COMMON, I18nNamespace.SOURCES]);
    const responsive = useActionBarResponsiveClasses();

    const hasTotal = totalCount !== undefined;
    const isAllSelected =
        hasTotal && totalCount > 0 && selectedCount === totalCount;
    const isIndeterminate = hasTotal
        ? totalCount > 0 && selectedCount > 0 && selectedCount < totalCount
        : selectedCount > 0;

    const disabledBulkActions: ActionConfig[] = bulkActions.map((action) =>
        action.type === ActionType.ITEM
            ? {
                  ...action,
                  disabled: selectedCount === 0 || action.disabled,
              }
            : action,
    );

    const checkboxChecked: boolean | 'indeterminate' = isAllSelected
        ? true
        : isIndeterminate
          ? 'indeterminate'
          : false;

    const shortCountLabel = hasTotal
        ? t('sources:selectionBar.selectedCountShort', {
              selected: selectedCount,
              total: totalCount,
          })
        : t('sources:selectionBar.selectedCountShortNoTotal', {
              selected: selectedCount,
          });
    const fullCountLabel = hasTotal
        ? t('common:selection.selectedCount', {
              selected: selectedCount,
              total: totalCount,
              entity: entityLabel ?? '',
          })
        : t('common:selection.selectedCountNoTotal', {
              selected: selectedCount,
              entity: entityLabel ?? '',
          });

    const actions: ActionConfig[] = [
        {
            type: ActionType.CUSTOM,
            content: (
                <Checkbox
                    checked={checkboxChecked}
                    onChange={onSelectAll}
                    variant={CheckboxVariant.WHITE}
                    label={t('browseBar.multiSelect')}
                    wrapperClassName="gap-1.5"
                    labelClassName="text-sm font-medium text-grey-100 whitespace-nowrap"
                />
            ),
        },
        {
            type: ActionType.CUSTOM,
            content: (
                <span className="text-sm-medium text-grey-300 whitespace-nowrap py-[6px] pl-[7px] pr-[11px]">
                    {useShortSelectionCount ? (
                        shortCountLabel
                    ) : (
                        <>
                            <span className={responsive.shortLabel}>
                                {shortCountLabel}
                            </span>
                            <span className={responsive.label}>
                                {fullCountLabel}
                            </span>
                        </>
                    )}
                </span>
            ),
        },
        ...disabledBulkActions,
        { type: ActionType.DIVIDER, orientation: DividerOrientation.VERTICAL },
        {
            type: ActionType.ITEM,
            label: t('common:selection.exit'),
            onClick: onExitMultiSelect,
        },
    ];

    return <ActionBar actions={actions} />;
}
